import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceItem } from '../../types/invoiceTypes';
import { ProductData } from '../../types/offerTypes';
import { IconMagic } from '../../icons';
import { enhanceInvoiceItemText } from '../../api/invoiceApi';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebaseConfig';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import logger, { nativeConsole } from '../../utils/logger';
import UnitCodeSelector from '../ui/UnitCodeSelector';
import { useProductManagement } from '../../hooks/invoice/useProductManagement';
import {
  TAX_CATEGORY_KEYS,
  getTaxCategoryEntry,
  resolveExemptionReason,
  extractParagraphNr,
  requiresZeroRateCategoryChoice,
  DEFAULT_TAX_CATEGORY_SELECTION,
  type InvoiceTaxCategoryKey,
} from '../../utils/taxCategoryCatalog';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS). Selektor nur sichtbar,
// wenn das PHP-Backend die Kategorie-Honorierung deployt hat (siehe Sequencing §10).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

// Tracking, welcher Preis zuletzt eingegeben wurde
interface PriceTracking {
  [itemId: string]: 'net' | 'gross';
}

interface InvoiceItemsFormProps {
  items: InvoiceItem[]; // Verwendet importierten Typ
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>; // Verwendet importierten Typ
  currency: string; // Währung hinzugefügt
  showTitle?: boolean;
  onProductSelect?: (itemId: string, selectedProduct: ProductData) => void; // NEU: Callback für externe Produkt-Auswahl
  // VB-1: rechnungsweiter Steuerart-Default für neue Positionen (nur bei aktivem Flag genutzt).
  defaultTaxCategorySelection?: InvoiceTaxCategoryKey | null;
  // VB-1: Käufer-USt-IdNr. — für den Inline-Hinweis bei K/AE-Positionen ohne USt-IdNr.
  buyerVatId?: string | null;
}

// Interface für KI-Vorschlag
interface AISuggestion {
  originalName: string;
  originalDescription?: string | null;
  suggestedName: string;
  suggestedDescription?: string | null;
}

// Autocomplete-Hook für Produkte
function useFirestoreAutocompleteProducts(searchValue: string, userId: string | undefined) {
  const [results, setResults] = useState<ProductData[]>([]);
  React.useEffect(() => {
    if (!userId || !searchValue || searchValue.length < 2) { setResults([]); return; }
    
    // Suchbegriffe für die client-seitige Filterung vorbereiten
    const searchLowerCase = searchValue.toLowerCase();
    const searchUpperCase = searchValue.toUpperCase();
    
    const fetch = async () => {
      try {
        const col = collection(db, 'products');
        const q = query(col, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        
        const matchingProducts = snapshot.docs
          .map(doc => ({ productId: doc.id, ...doc.data() } as ProductData))
          .filter(product => {
            // Stelle sicher, dass name existiert und ein String ist
            const productName = product.name?.toString() || '';
            // Erweiterte Teilstring-Übereinstimmung (case-insensitive)
            return productName.toLowerCase().includes(searchLowerCase) || 
                   productName.toUpperCase().includes(searchUpperCase);
          })
          .slice(0, 7); // Beschränke auf 7 Ergebnisse für die UI
          
        logger.debug({count: matchingProducts.length}, "gefundene produktvorschläge");
        setResults(matchingProducts);
      } catch (error) {
        logger.error(error, "fehler beim abrufen der produkt-vorschläge");
        setResults([]);
      }
    };
    
    fetch();
  }, [userId, searchValue]);
  
  return results;
}

const sanitizeSignedDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^0-9,.\-]/g, '');
  const hasLeadingMinus = cleaned.startsWith('-');
  const withoutMinus = cleaned.replace(/-/g, '');
  const firstSeparatorIndex = withoutMinus.search(/[,.]/);
  let normalized = withoutMinus;

  if (firstSeparatorIndex !== -1) {
    const before = withoutMinus.slice(0, firstSeparatorIndex + 1);
    const after = withoutMinus.slice(firstSeparatorIndex + 1).replace(/[,.]/g, '');
    normalized = before + after;
    return `${hasLeadingMinus ? '-' : ''}${normalized}`;
  }

  return `${hasLeadingMinus ? '-' : ''}${normalized}`;
};

const ItemRow: React.FC<{
  item: InvoiceItem;
  onItemChange: (id: string, field: keyof Omit<InvoiceItem, 'id'>, value: any) => void;
  onRemove: (id: string) => void;
  currency: string;
  productNameInput: string;
  setProductNameInput: (value: string) => void;
  showProductDropdown: boolean;
  setShowProductDropdown: (show: boolean) => void;
  onSelectProductSuggestion: (suggestion: ProductData) => void;
  userId: string | undefined;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  lastEditedPrice: 'net' | 'gross' | undefined;
  onEnhanceClick: (id: string) => void;
  isLoadingSuggestion: boolean;
  index: number;
  formatCurrency: (amount: number) => string;
  calculateItemNetAmount: (item: InvoiceItem) => number;
  calculateGrossPrice: (netPrice: number | undefined | null, taxRate: number) => number;
  calculateNetPrice: (grossPrice: number | undefined | null, taxRate: number) => number;
  t: (key: string) => string;
  buyerVatId?: string | null;
}> = ({
  item,
  onItemChange,
  onRemove,
  currency,
  productNameInput,
  setProductNameInput,
  showProductDropdown,
  setShowProductDropdown,
  onSelectProductSuggestion,
  userId,
  expanded,
  onToggleExpand,
  lastEditedPrice,
  onEnhanceClick,
  isLoadingSuggestion,
  index,
  formatCurrency,
  calculateItemNetAmount,
  calculateGrossPrice,
  calculateNetPrice,
  t,
  buyerVatId,
}) => {
  const { t: tCommon } = useTranslation('common');

  // --- VB-1: Steuerart (BT-151) ---
  const taxCategoryEntry = getTaxCategoryEntry(item.taxCategorySelection);
  const taxRateLocked = TAX_CATEGORY_ENABLED && !!taxCategoryEntry?.ratesLocked;
  // 0 %-Satz ohne eindeutige 0 %-Kategorie ⇒ prominente Pflicht-Auswahl inline.
  const needsZeroRateChoice =
    TAX_CATEGORY_ENABLED &&
    requiresZeroRateCategoryChoice(item.taxRate, item.taxCategorySelection);
  // Aufgelöster Pflicht-Hinweis (BT-121) bzw. optionaler §12(3)-BT-22-Hinweis.
  const mandatoryNote = taxCategoryEntry
    ? (item.exemptionReason || taxCategoryEntry.bt22HintText || null)
    : null;
  const buyerVatMissing =
    !!taxCategoryEntry?.requiresBuyerVatId && !(buyerVatId ?? '').trim();
  // Eine 0 %-gesperrte Kategorie (Z/E/AE/K/G) ist gewählt — Satz ist auf 0 fixiert.
  const isZeroCategorySelected = TAX_CATEGORY_ENABLED && !!taxCategoryEntry?.ratesLocked;
  // Das inline Steuerart-Panel erscheint NUR im 0 %-Kontext (offene Pflichtauswahl
  // ODER bereits gewählte 0 %-Kategorie). Bei eindeutigem 19/7-Satz bleibt es
  // unsichtbar — kein Selektor, keine Reibung für den Standardfall.
  const showTaxPanel = needsZeroRateChoice || isZeroCategorySelected;
  // Wert des Selektors: leer (Platzhalter „Bitte Steuerart wählen") bis eine
  // eindeutige 0 %-Kategorie gewählt wurde (kein stiller Default „standard").
  const taxPanelSelectValue = isZeroCategorySelected ? taxCategoryEntry!.selection : '';

  /**
   * Setzt eine neue Steuerart auf der Position: BT-151-Code, Befreiungsgrund und
   * (bei 0 %-Kategorien) den gesperrten USt-Satz. Bestehende §4-Nr. bleibt erhalten.
   * Wird `standard` aus dem 0 %-Kontext gewählt, wird der Satz wieder auf 19 %
   * freigegeben (verlässt die 0 %-Zone, blendet das Pflicht-Panel aus).
   */
  const handleTaxCategorySelect = (key: string) => {
    const entry = getTaxCategoryEntry(key);
    if (!entry) return;
    onItemChange(item.id, 'taxCategorySelection', entry.selection);
    onItemChange(item.id, 'taxCategory', entry.bt151Code);
    const existingNr = extractParagraphNr(entry.selection, item.exemptionReason);
    onItemChange(item.id, 'exemptionReason', resolveExemptionReason(entry.selection, existingNr));
    if (entry.ratesLocked) {
      onItemChange(item.id, 'taxRate', entry.allowedRates[0]);
    } else if (Number(item.taxRate) === 0) {
      onItemChange(item.id, 'taxRate', 19);
    }
  };

  /** Aktualisiert den §4-Befreiungstext anhand der eingegebenen konkreten Nr. */
  const handleParagraphNrChange = (nr: string) => {
    onItemChange(item.id, 'exemptionReason', resolveExemptionReason('exempt_4', nr));
  };
  const productSuggestions = useFirestoreAutocompleteProducts(productNameInput, userId);

  // Refs für Felder, um aktiven Fokus zu verfolgen und wiederherzustellen
  const activeFieldRef = useRef<HTMLInputElement | null>(null);
  const netPriceRef = useRef<HTMLInputElement>(null);
  const grossPriceRef = useRef<HTMLInputElement>(null);
  const taxRateRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  
  // Calculate potentially precise display values based on stored item data and passed functions
  const lastEditedType = lastEditedPrice; 
  const preciseDisplayGrossPrice = lastEditedType === 'gross' ? item.grossPrice : calculateGrossPrice(item.netPrice, item.taxRate);
  const preciseDisplayNetPrice = lastEditedType === 'net' ? item.netPrice : calculateNetPrice(item.grossPrice, item.taxRate);

  // Calculate the accurate net amount for THIS line item using the passed function
  const lineItemNetAmount = calculateItemNetAmount(item);

  // Hilfsfunktion zur formatierung von Preisen für die Anzeige
  const formatPriceForDisplay = (value: number | undefined | null, isEditing: boolean = false, fieldKey?: string): string => {
    // Wenn wir gerade editieren und ein String-Input existiert, zeige diesen
    if (isEditing && fieldKey && priceInputs[fieldKey] !== undefined) {
      return priceInputs[fieldKey];
    }
    
    if (value === null || value === undefined) return '';
    // Wenn wir gerade editieren, zeige den rohen Wert ohne Formatierung
    if (isEditing) {
      return value.toString();
    }
    // Sonst formatiere mit 2 Nachkommastellen
    return value.toFixed(2);
  };

  // Helper to round only for display in input fields
  const roundForDisplay = (value: number | undefined | null): number | string => {
    if (value === null || value === undefined) return '';
    return value;
  };

  // State für aktive Eingabefelder (um zu wissen, welche gerade editiert werden)
  const [editingFields, setEditingFields] = useState<{ [key: string]: boolean }>({});
  // State für Preiseingaben als String während der Bearbeitung
  const [priceInputs, setPriceInputs] = useState<{ [key: string]: string }>({});
  const [isEditingQuantity, setIsEditingQuantity] = useState<boolean>(false);
  const [quantityInput, setQuantityInput] = useState<string>('');

  // Verbesserte Fokus-Behandlung mit Verzögerung
  // Speichert aktives Element für spätere Wiederherstellung
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>, fieldKey?: string) => {
    activeFieldRef.current = event.target;
    event.target.select();
    
    // Markiere das Feld als "wird editiert"
    if (fieldKey) {
      setEditingFields(prev => ({ ...prev, [fieldKey]: true }));
      
      // Initialisiere String-Input für Preisfelder
      if (fieldKey.includes('Price')) {
        const currentValue = fieldKey.includes('netPrice') ? item.netPrice : item.grossPrice;
        setPriceInputs(prev => ({ 
          ...prev, 
          [fieldKey]: currentValue?.toString() || '' 
        }));
      }
    }
  };

  // Neuer onBlur-Handler für Preisformatierung
  const handlePriceBlur = (field: 'netPrice' | 'grossPrice', fieldKey: string) => {
    const stringValue = priceInputs[fieldKey] || '';
    
    if (stringValue.trim() === '' || stringValue.trim() === '-') {
      onItemChange(item.id, field, null);
    } else {
      // Ersetze Komma durch Punkt und parse als Zahl
      const numericValue = parseFloat(stringValue.replace(',', '.'));
      if (!isNaN(numericValue)) {
        const formattedValue = Math.round(numericValue * 100) / 100;
        onItemChange(item.id, field, formattedValue);
      }
    }
    
    // Markiere das Feld als "nicht mehr editiert"
    setEditingFields(prev => ({ ...prev, [fieldKey]: false }));
    // Entferne String-Input
    setPriceInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[fieldKey];
      return newInputs;
    });
  };

  // Handler für Preisänderungen während der Eingabe
  const handlePriceChange = (fieldKey: string, value: string) => {
    // Erlaube Zahlen, Komma und Punkt
    const sanitizedValue = sanitizeSignedDecimalInput(value);
    setPriceInputs(prev => ({ ...prev, [fieldKey]: sanitizedValue }));
  };

  const handleQuantityFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(event);
    setIsEditingQuantity(true);
    setQuantityInput(item.quantity?.toString() || '');
  };

  const handleQuantityChange = (value: string) => {
    const sanitizedValue = sanitizeSignedDecimalInput(value);
    setQuantityInput(sanitizedValue);
    const numericValue = parseFloat(sanitizedValue.replace(',', '.'));
    if (!Number.isNaN(numericValue)) {
      handleFieldChange('quantity', numericValue);
    }
  };

  const handleQuantityBlur = () => {
    const stringValue = quantityInput.trim();
    if (stringValue === '' || stringValue === '-') {
      onItemChange(item.id, 'quantity', 0);
    } else {
      const numericValue = parseFloat(stringValue.replace(',', '.'));
      if (!Number.isNaN(numericValue)) {
        onItemChange(item.id, 'quantity', numericValue);
      }
    }
    setIsEditingQuantity(false);
    setQuantityInput('');
  };

  // Verbesserte Change-Handlers mit Fokus-Beibehaltung
  const handleFieldChange = (field: keyof Omit<InvoiceItem, 'id'>, value: any) => {
    // Debug-Logging für erweiterte Felder
    if (['unitCode', 'sellerArticleId', 'buyerArticleId', 'globalId', 'globalIdScheme'].includes(field)) {
      console.log(`DEBUG: Changing ${field} to:`, value, 'for item:', item.id);
    }
    
    // Fokus-Element merken, aber nicht für die Beschreibung, da textarea
    const activeElement = field !== 'description' ? activeFieldRef.current : null;
    
    // Dann den Wert ändern
    onItemChange(item.id, field, value);
    
    // Nur wenn nicht bei der Beschreibung und ein aktives Element existiert
    // den Fokus wiederherstellen
    if (activeElement && field !== 'description') {
      // Timeout ermöglicht es React, das Rendering abzuschließen, bevor wir den Fokus wiederherstellen
      setTimeout(() => {
        // Nach der Änderung den Fokus wiederherstellen
        activeElement.focus();
      }, 0);
    }
  };

  return (
    <div className="border border-strong rounded-md p-5 mb-6 bg-surface-inset shadow-sm relative hover:shadow-md transition-shadow duration-200">
      {/* Positionsnummer Badge */}
      <div className="absolute -top-3 -left-3 bg-indigo-600 text-[#0C1322] rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold shadow-md">
        {index + 1}
      </div>
      
      {/* Entfernen-Button in der oberen rechten Ecke */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="absolute top-2 right-2 text-muted hover:text-red-500 transition-colors"
        title="Position entfernen"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {/* Hauptbereich: Produktname und Beschreibung */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-2">
          {/* Produktname mit Autocomplete */}
          <div className="flex-grow">
            <label htmlFor={`itemName-${item.id}`} className="block text-sm font-medium text-secondary">
              {t('positions.name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1 flex">
              <input
                type="text"
                id={`itemName-${item.id}`}
                name="name"
                value={productNameInput !== undefined ? productNameInput : item.name}
                onChange={e => { 
                  setProductNameInput(e.target.value); 
                  onItemChange(item.id, 'name', e.target.value);
                  setShowProductDropdown(true);
                }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                className="input-field placeholder-visible"
                                    placeholder={t('positions.namePlaceholder')}
                autoComplete="off"
                required
              />
              <button
                type="button"
                onClick={() => onEnhanceClick(item.id)}
                className={`ml-2 inline-flex items-center rounded-md border px-2.5 py-1.5 text-xs font-medium shadow-sm ${isLoadingSuggestion ? 'border-gray-500 bg-surface-hover text-muted cursor-not-allowed' : 'border-indigo-500 bg-surface-raised text-accent-ink hover:bg-surface-inset'}`}
                title="Name & Beschreibung verbessern (KI)"
                disabled={isLoadingSuggestion}
              >
                {isLoadingSuggestion ? (
                  <svg className="animate-spin h-4 w-4 text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <IconMagic size={12} />
                )}
              </button>
            </div>
            {showProductDropdown && productSuggestions && productSuggestions.length > 0 && (
              <ul className="absolute z-10 bg-surface-hover border border-gray-500 rounded-md shadow-lg w-full mt-1 max-h-60 overflow-auto divide-y divide-gray-500">
                {productSuggestions.map(s => (
                  <li 
                    key={s.productId} 
                    className="px-4 py-2 hover:bg-gray-500 cursor-pointer text-sm text-primary transition-colors duration-150" 
                    onMouseDown={() => onSelectProductSuggestion(s)}
                  >
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted flex justify-between">
                      <span>{s.description || ''}</span>
                      {s.taxRate !== undefined && <span className="font-semibold">{s.taxRate}% MwSt</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Beschreibung */}
        <div className="mt-3">
          <label htmlFor={`item-description-${item.id}`} className="block text-sm font-medium text-secondary">
            {t('positions.description')}
          </label>
          <textarea
            id={`item-description-${item.id}`}
            rows={1}
            value={item.description || ''}
            onChange={e => handleFieldChange('description', e.target.value || null)}
            className="input-field placeholder-visible"
                                placeholder={t('positions.descriptionPlaceholder')}
          />
        </div>
      </div>
      
      {/* Mengen- und Preisbereich - 5 columns, all inputs h-10 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
        {/* Menge */}
        <div>
          <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
            {t('positions.quantity')} <span className="text-red-500">*</span>
          </label>
          <input
            ref={quantityRef}
            type="text"
            inputMode="decimal"
            value={isEditingQuantity ? quantityInput : item.quantity}
            onChange={e => handleQuantityChange(e.target.value)}
            required
            onFocus={handleQuantityFocus}
            onBlur={handleQuantityBlur}
            className="input-field placeholder-visible h-10"
            placeholder="1"
          />
        </div>
        
        {/* Einzelpreis (Netto) */}
        <div>
          <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
            {t('positions.unitPriceNet')}
          </label>
          <input
            ref={netPriceRef}
            type="text"
            value={formatPriceForDisplay(preciseDisplayNetPrice, editingFields[`netPrice-${item.id}`], `netPrice-${item.id}`)}
            onChange={e => handlePriceChange(`netPrice-${item.id}`, e.target.value)}
            onFocus={(e) => handleFocus(e, `netPrice-${item.id}`)}
            onBlur={(e) => handlePriceBlur('netPrice', `netPrice-${item.id}`)}
            className="input-field placeholder-visible h-10"
            placeholder="0.00"
          />
        </div>
        
        {/* Steuersatz - Ensured background and height */}
        <div>
          <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
            {t('positions.vat')}
          </label>
          <input
            ref={taxRateRef}
            type="number"
            step="0.1"
            value={item.taxRate}
            onChange={e => handleFieldChange('taxRate', parseFloat(e.target.value) || 0)}
            onFocus={handleFocus}
            disabled={taxRateLocked}
            title={taxRateLocked ? t('taxCategory.rateLockedHint') : undefined}
            className={`input-field placeholder-visible h-10 ${taxRateLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
            placeholder="19"
          />
        </div>

        {/* Bruttopreis */}
        <div>
          <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
            {t('positions.unitPriceGross')}
          </label>
          <input
            ref={grossPriceRef}
            type="text"
            value={formatPriceForDisplay(preciseDisplayGrossPrice, editingFields[`grossPrice-${item.id}`], `grossPrice-${item.id}`)}
            onChange={e => handlePriceChange(`grossPrice-${item.id}`, e.target.value)}
            onFocus={(e) => handleFocus(e, `grossPrice-${item.id}`)}
            onBlur={(e) => handlePriceBlur('grossPrice', `grossPrice-${item.id}`)}
            className="input-field placeholder-visible h-10"
            placeholder="0.00"
          />
        </div>
        
        {/* Gesamt (Netto) - Ensured height */}
        <div>
          <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
            {t('positions.totalNet')}
          </label>
          <p className="mt-1 block w-full px-3 py-2 border border-strong bg-[rgb(var(--surface-base-rgb)/0.5)] text-primary rounded-md sm:text-sm h-10 flex items-center">
            {formatCurrency(lineItemNetAmount)}
          </p>
        </div>
      </div>
      
      {/* VB-1: Inline Steuerart-Panel — erscheint NUR im 0 %-Kontext (sobald der
          Satz 0 ist bzw. eine 0 %-Kategorie gewählt wurde). Für eindeutige 19/7-
          Sätze bleibt es unsichtbar (kein Selektor, keine Reibung). Bei offener
          Pflichtauswahl auffällig amber + Platzhalter ohne Voreinstellung; nach
          eindeutiger Wahl ruhig indigo mit BT-121-Pflichtwortlaut. */}
      {showTaxPanel && (
        <div
          className={`mb-3 rounded-md px-4 py-3 ${
            needsZeroRateChoice
              ? 'border-2 border-amber-500/70 bg-[var(--tone-warning-bg)]'
              : 'border border-[var(--tone-accent-border)] bg-[var(--tone-accent-bg)]'
          }`}
        >
          <div className="flex items-start gap-2">
            {needsZeroRateChoice && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 text-[var(--tone-warning-fg)] mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <div className="flex-grow">
              {needsZeroRateChoice ? (
                <>
                  <p className="text-sm font-semibold text-[var(--tone-warning-fg)]">
                    {t('taxCategory.zeroRateTitle')}
                  </p>
                  <p className="mt-1 text-xs text-[var(--tone-warning-fg)]">
                    {t('taxCategory.zeroRateHint')}
                  </p>
                </>
              ) : (
                <p className="text-xs font-semibold text-accent-ink">
                  {t('taxCategory.label')}
                </p>
              )}
              <div className="mt-2">
                <label htmlFor={`taxCategory-${item.id}`} className="sr-only">
                  {t('taxCategory.label')}
                </label>
                <select
                  id={`taxCategory-${item.id}`}
                  value={taxPanelSelectValue}
                  onChange={(e) => handleTaxCategorySelect(e.target.value)}
                  className={`input-field ${needsZeroRateChoice ? 'border-amber-500 text-[var(--tone-warning-fg)]' : ''}`}
                >
                  <option value="" disabled>
                    {t('taxCategory.zeroRatePlaceholder')}
                  </option>
                  {TAX_CATEGORY_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {t(`taxCategory.options.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
              {taxCategoryEntry?.requiresParagraphNr && (
                <div className="mt-2">
                  <label
                    htmlFor={`paragraphNr-${item.id}`}
                    className="block text-xs text-secondary mb-1"
                  >
                    {t('taxCategory.paragraphNrLabel')} <span className="text-[var(--tone-danger-fg)]">*</span>
                  </label>
                  <input
                    id={`paragraphNr-${item.id}`}
                    type="text"
                    value={extractParagraphNr(item.taxCategorySelection, item.exemptionReason)}
                    onChange={(e) => handleParagraphNrChange(e.target.value)}
                    className="input-field text-sm"
                    placeholder={t('taxCategory.paragraphNrPlaceholder')}
                  />
                </div>
              )}
              {/* BT-121-Pflichtwortlaut bzw. §12(3)-BT-22-Hinweis, sobald aufgelöst. */}
              {!needsZeroRateChoice && mandatoryNote && (
                <p className="mt-2 text-sm text-[var(--tone-accent-fg)]">{mandatoryNote}</p>
              )}
              {buyerVatMissing && (
                <p className="mt-2 text-sm text-[var(--tone-danger-fg)]">{t('taxCategory.validation.buyerVatIdRequired')}</p>
              )}
              {!needsZeroRateChoice && (
                <p className="mt-2 text-xs text-muted">{t('taxCategory.responsibilityHint')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expander für weitere Felder */}
      <div className="text-right">
        <button
          type="button"
          onClick={() => onToggleExpand(item.id)}
          className="text-xs text-accent-ink hover:text-accent-ink"
        >
                            {expanded ? t('positions.lessOptions') : t('positions.moreOptions')}
        </button>
      </div>

      {/* Zusätzliche Felder (wenn expanded) */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-strong grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* VB-1: Der Steuerart-Selektor sitzt nicht mehr hier unter den
              Zusatzeinstellungen, sondern erscheint prominent inline am Posten,
              sobald der USt-Satz 0 ist (siehe Panel oben). */}
          {/* Einheit */}
          <div>
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">{t('positions.unit')}</label>
            <UnitCodeSelector
              value={item.unitCode || null}
              onChange={(code) => {
                console.log('DEBUG unitCode onChange:', code, 'for item:', item.id);
                console.log('DEBUG current item.unitCode:', item.unitCode);
                onItemChange(item.id, 'unitCode', code);
              }}
              placeholder={t('positions.unitPlaceholder') || 'Einheit wählen...'}
            />
          </div>
          {/* Artikelnummer Verkäufer */}
          <div>
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">{t('positions.sellerItemRef')} (Opt.)</label>
             <input
              type="text"
              value={item.sellerArticleId || ''}
              onChange={e => {
                console.log('DEBUG sellerArticleId onChange:', e.target.value, 'for item:', item.id);
                console.log('DEBUG current item.sellerArticleId:', item.sellerArticleId);
                onItemChange(item.id, 'sellerArticleId', e.target.value || null);
              }}
              onFocus={handleFocus}
              className="input-field placeholder-visible"
                              placeholder={t('positions.sellerItemRefPlaceholder')}
            />
          </div>
          {/* Art-Nr. Käufer */}
          <div>
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">{t('positions.buyerItemRef')} (Opt.)</label>
            <input
              type="text"
              value={item.buyerArticleId || ''}
              onChange={e => {
                console.log('DEBUG buyerArticleId onChange:', e.target.value, 'for item:', item.id);
                console.log('DEBUG current item.buyerArticleId:', item.buyerArticleId);
                onItemChange(item.id, 'buyerArticleId', e.target.value || null);
              }}
              onFocus={handleFocus}
              className="input-field placeholder-visible"
                              placeholder={t('positions.buyerItemRefPlaceholder')}
            />
          </div>
          {/* Globale ID */}
          <div>
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">{t('positions.globalId')} (Opt.)</label>
            <input
              type="text"
              value={item.globalId || ''}
              onChange={e => {
                console.log('DEBUG globalId onChange:', e.target.value, 'for item:', item.id);
                console.log('DEBUG current item.globalId:', item.globalId);
                onItemChange(item.id, 'globalId', e.target.value || null);
              }}
              onFocus={handleFocus}
              className="input-field placeholder-visible"
                              placeholder={t('positions.globalIdPlaceholder')}
            />
          </div>
           {/* Schema Globale ID */}
           <div>
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">{t('positions.globalIdScheme')} (Opt.)</label>
            <input
              type="text"
              value={item.globalIdScheme || ''}
              onChange={e => {
                console.log('DEBUG globalIdScheme onChange:', e.target.value, 'for item:', item.id);
                console.log('DEBUG current item.globalIdScheme:', item.globalIdScheme);
                onItemChange(item.id, 'globalIdScheme', e.target.value || null);
              }}
              onFocus={handleFocus}
              className="input-field placeholder-visible"
                              placeholder={t('positions.globalIdSchemePlaceholder')}
            />
          </div>
          {/* Positionsrabatt */}
          <div className="md:col-span-3 border-t border-strong pt-3 mt-1">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id={`discount-toggle-${item.id}`}
                checked={!!item.discount}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Listenpreis = bisheriger netPrice, Rabatt = 0%
                    onItemChange(item.id, 'listPrice', item.netPrice);
                    onItemChange(item.id, 'discount', { type: 'percentage', value: 0, reason: '' });
                  } else {
                    onItemChange(item.id, 'listPrice', null);
                    onItemChange(item.id, 'discount', null);
                  }
                }}
                className="w-4 h-4 accent-indigo-500"
              />
              <label htmlFor={`discount-toggle-${item.id}`} className="text-sm font-medium text-secondary">
                {tCommon('positions.discountToggle')}
              </label>
            </div>
            {item.discount && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pl-6">
                {/* Listenpreis */}
                <div>
                  <label className="block text-xs text-muted mb-1">
                    {tCommon('positions.listPrice')}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.listPrice ?? item.netPrice ?? 0}
                    onChange={(e) => {
                      const lp = parseFloat(e.target.value) || 0;
                      onItemChange(item.id, 'listPrice', lp);
                      // Recalculate netPrice
                      const disc = item.discount!;
                      const newNet = disc.type === 'percentage'
                        ? Math.round(lp * (1 - disc.value / 100) * 100) / 100
                        : Math.round((lp - disc.value) * 100) / 100;
                      onItemChange(item.id, 'netPrice', Math.max(0, newNet));
                    }}
                    className="input-field text-sm"
                  />
                </div>
                {/* Rabatt Typ */}
                <div>
                  <label className="block text-xs text-muted mb-1">Typ</label>
                  <select
                    value={item.discount.type}
                    onChange={(e) => {
                      const newDisc = { ...item.discount!, type: e.target.value as 'percentage' | 'amount' };
                      onItemChange(item.id, 'discount', newDisc);
                      const lp = item.listPrice ?? item.netPrice ?? 0;
                      const newNet = newDisc.type === 'percentage'
                        ? Math.round(lp * (1 - newDisc.value / 100) * 100) / 100
                        : Math.round((lp - newDisc.value) * 100) / 100;
                      onItemChange(item.id, 'netPrice', Math.max(0, newNet));
                    }}
                    className="input-field text-sm"
                  >
                    <option value="percentage">{tCommon('positions.discountPercent')}</option>
                    <option value="amount">{tCommon('positions.discountAmount')}</option>
                  </select>
                </div>
                {/* Rabattwert */}
                <div>
                  <label className="block text-xs text-muted mb-1">
                    {item.discount.type === 'percentage'
                      ? tCommon('positions.discountPercent')
                      : tCommon('positions.discountAmount')}
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={item.discount.type === 'percentage' ? 100 : undefined}
                    step={item.discount.type === 'percentage' ? 0.1 : 0.01}
                    value={item.discount.value}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      const newDisc = { ...item.discount!, value: val };
                      onItemChange(item.id, 'discount', newDisc);
                      const lp = item.listPrice ?? item.netPrice ?? 0;
                      const newNet = newDisc.type === 'percentage'
                        ? Math.round(lp * (1 - val / 100) * 100) / 100
                        : Math.round((lp - val) * 100) / 100;
                      onItemChange(item.id, 'netPrice', Math.max(0, newNet));
                    }}
                    className="input-field text-sm"
                  />
                </div>
                {/* Grund */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-muted mb-1">
                    {tCommon('positions.discountReason')}
                  </label>
                  <input
                    type="text"
                    value={item.discount.reason ?? ''}
                    onChange={(e) => onItemChange(item.id, 'discount', { ...item.discount!, reason: e.target.value })}
                    className="input-field text-sm placeholder-subtle"
                    placeholder="z.B. Mengenrabatt"
                  />
                </div>
                {/* Preis nach Rabatt */}
                <div>
                  <label className="block text-xs text-muted mb-1">
                    {tCommon('positions.priceAfterDiscount')}
                  </label>
                  <p className="mt-1 block w-full px-3 py-2 border border-strong bg-[rgb(var(--surface-base-rgb)/0.5)] text-accent-ink rounded-md text-sm">
                    {formatCurrency(item.netPrice ?? 0)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* UN/ECE Rec20 Codes Hint */}
           <div className="md:col-span-3">
             <p className="text-xs text-muted">
               {t('positions.unitsHelp')}
             </p>
           </div>
        </div>
      )}
    </div>
  );
};

const InvoiceItemsForm: React.FC<InvoiceItemsFormProps> = ({ items, setItems, currency, showTitle = true, onProductSelect, defaultTaxCategorySelection, buyerVatId }) => {
  const { t } = useTranslation('invoice');
  const { currentUser, sellerData, effectiveScopeUid } = useAuth();
  const [lastEditedPrice, setLastEditedPrice] = useState<PriceTracking>({});
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [productNameInputs, setProductNameInputs] = useState<{ [id: string]: string }>({});
  const [showProductDropdowns, setShowProductDropdowns] = useState<{ [id: string]: boolean }>({});
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null);
  const [enhancingItemId, setEnhancingItemId] = useState<string | null>(null);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState<boolean>(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState<boolean>(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  // Initialize the product management hook
  const {
    handleProductSelection: hookHandleProductSelection,
    isLoading: productMgmtLoading,
    error: productMgmtError,
    resetProductManagement
  } = useProductManagement();

  // Neuer useEffect-Hook, der sicherstellt, dass productNameInputs immer aktuell sind
  useEffect(() => {
    // Aktualisiere die productNameInputs mit den Namen aus dem items-Array
    const updatedInputs: { [id: string]: string } = {};
    items.forEach(item => {
      // Setze den Namen nur, wenn er nicht bereits im productNameInputs State ist
      if (!productNameInputs[item.id] || productNameInputs[item.id] !== item.name) {
        updatedInputs[item.id] = item.name;
      }
    });

    // Nur aktualisieren, wenn es tatsächlich Änderungen gibt
    if (Object.keys(updatedInputs).length > 0) {
      setProductNameInputs(prev => ({
        ...prev,
        ...updatedInputs
      }));
    }
  }, [items]);

  // Zusätzlicher useEffect, der lastEditedPrice für neue Items initialisiert
  useEffect(() => {
    // Prüfe auf neue Items, für die noch kein lastEditedPrice existiert
    const newItemsWithoutTracking = items.filter(item => !lastEditedPrice[item.id]);
    
    if (newItemsWithoutTracking.length > 0) {
  
      
      // Erstelle ein Objekt mit den neuen Items
      const newTracking: PriceTracking = {};
      newItemsWithoutTracking.forEach(item => {
        // Standard: Wenn sowohl netPrice als auch grossPrice definiert sind,
        // bevorzuge netPrice als Ausgangspunkt, ausser grossPrice ist explizit gesetzt
        if (item.grossPrice !== undefined && item.grossPrice !== null && item.grossPrice > 0) {
          newTracking[item.id] = 'gross';
          logger.debug(`Item ${item.id} (${item.name}): lastEditedPrice auf 'gross' gesetzt (grossPrice=${item.grossPrice})`);
        } else {
          newTracking[item.id] = 'net';

        }
      });
      
      // Aktualisiere den State
      setLastEditedPrice(prev => ({
        ...prev,
        ...newTracking
      }));
    }
  }, [items, lastEditedPrice]);

  // Hilfsfunktion zum Runden auf 2 Dezimalstellen
  const roundToTwoDecimals = (value: number): number => {
    // This helper is now ONLY for final display rounding
    return Math.round(value * 100) / 100;
  };

  // Remove internal rounding
  const calculateGrossPrice = (netPrice: number | undefined | null, taxRate: number): number => {
    return (netPrice || 0) * (1 + taxRate / 100);
  };

  // Remove internal rounding
  const calculateNetPrice = (grossPrice: number | undefined | null, taxRate: number): number => {
    if (taxRate === -100) return 0; 
    return (grossPrice || 0) / (1 + taxRate / 100);
  };

  // Determines the accurate net price for a single unit based on tracking - Remove internal rounding
  const getAccurateSingleNetPrice = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const lastEdited = trackingState[item.id] || 'net'; 
    if (lastEdited === 'gross') {
      return calculateNetPrice(item.grossPrice, item.taxRate); // Uses precise calculation
    } else {
      return item.netPrice || 0;
    }
  };

  // Calculates the total net amount for the line item - Remove internal rounding
  const getAccurateItemNetAmount = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const singleNetPrice = getAccurateSingleNetPrice(item, trackingState);
    return item.quantity * singleNetPrice; // Use precise multiplication
  };

  // Calculates the total tax amount for the line item - Remove internal rounding
  const getAccurateItemTaxAmount = (item: InvoiceItem, trackingState: PriceTracking): number => {
    const netAmount = getAccurateItemNetAmount(item, trackingState); // Uses precise net amount
    return netAmount * (item.taxRate / 100); // Use precise multiplication
  };

  // --- Calculations for Totals (using accurate helpers, rounding at the end) --- 
  const calculateItemNetAmountForTotal = (item: InvoiceItem): number => { 
    return getAccurateItemNetAmount(item, lastEditedPrice); 
  };
  const calculateItemTaxAmountForTotal = (item: InvoiceItem): number => { 
    return getAccurateItemTaxAmount(item, lastEditedPrice); 
  };
  
  // Calculate precise totals by summing precise line item amounts
  const preciseNetTotal = items.reduce((sum, item) => sum + calculateItemNetAmountForTotal(item), 0);
  const preciseTaxTotal = items.reduce((sum, item) => sum + calculateItemTaxAmountForTotal(item), 0);
  // Gross total derived from precise net and tax sums
  const preciseGrossTotal = preciseNetTotal + preciseTaxTotal; 

  // Round totals *only* for display
  const displayGrossTotal = roundToTwoDecimals(preciseGrossTotal);
  const displayTaxTotal = roundToTwoDecimals(preciseTaxTotal);
  // Derive displayNetTotal from rounded values to ensure visual consistency
  const displayNetTotal = roundToTwoDecimals(displayGrossTotal - displayTaxTotal);

  // Group taxes by rate for display
  const taxesByRate = items.reduce((acc, item) => {
    const rate = item.taxRate;
    // Use precise calculations for grouping taxes
    const netAmount = calculateItemNetAmountForTotal(item);
    const taxAmount = calculateItemTaxAmountForTotal(item);
    if (!acc[rate]) {
      acc[rate] = { net: 0, tax: 0 };
    }
    // Sum precise amounts for each rate
    acc[rate].net += netAmount;
    acc[rate].tax += taxAmount;
    return acc;
  }, {} as { [key: number]: { net: number; tax: number } });

  // Funktion zum Hinzufügen einer neuen, leeren Position
  const handleAddItem = () => {
    // Hole Standardwerte aus dem Seller-Kontext oder setze Fallbacks
    let defaultTax = 19.0;
    
    // Wenn Kleinunternehmer, dann 0% als Standard
    if (sellerData?.isSmallBusiness) {
      defaultTax = 0.0;
    } else if (sellerData?.defaultTaxRate !== undefined && sellerData?.defaultTaxRate !== null) {
      defaultTax = sellerData.defaultTaxRate;
    }

    const defaultUnit = sellerData?.defaultUnitCode || 'H87';

    // VB-1: Steuerart-Default für neue Positionen (nur bei aktivem Flag).
    const defaultSelection = TAX_CATEGORY_ENABLED
      ? (defaultTaxCategorySelection || DEFAULT_TAX_CATEGORY_SELECTION)
      : undefined;
    const defaultEntry = getTaxCategoryEntry(defaultSelection);
    // Bei 0 %-Default den gesperrten Satz übernehmen, sonst den Seller-Default.
    const effectiveTax = defaultEntry?.ratesLocked ? defaultEntry.allowedRates[0] : defaultTax;

    const newItem: InvoiceItem = {
      id: crypto.randomUUID(), // Eindeutige ID für React
      name: '',
      quantity: 1,
      netPrice: 0,
      taxRate: effectiveTax,
      unitPrice: 0, // Wird ggf. von netPrice abgeleitet, hier initial 0
      unitCode: defaultUnit,
      description: '', // Leere Beschreibung initial
      itemAction: 'create', // Standardaktion für neue Items
      productId: null,
      // VB-1: Steuerart-Felder vorbelegen (additiv; undefined wenn Flag aus).
      ...(defaultEntry && {
        taxCategorySelection: defaultEntry.selection,
        taxCategory: defaultEntry.bt151Code,
        exemptionReason: resolveExemptionReason(defaultEntry.selection),
      }),
      // Weitere optionale Felder sind initial null/undefined
    };
    
    // Alle State-Updates in einem Batch durchführen, um Race Conditions zu vermeiden
    const newItemId = newItem.id;
    
    // Füge das neue Item zum items-Array hinzu
    setItems([...items, newItem]);
    
    // Initialisiere die Tracking-States für das neue Item außerhalb des Renderings
    const newInputs = { ...productNameInputs, [newItemId]: '' };
    const newDropdowns = { ...showProductDropdowns, [newItemId]: false };
    const newPriceTracking = { ...lastEditedPrice, [newItemId]: 'net' as 'net' };
    
    setProductNameInputs(newInputs);
    setShowProductDropdowns(newDropdowns); 
    setLastEditedPrice(newPriceTracking);
    setExpandedItemId(newItemId); // Neue Zeile direkt aufklappen
  };

  // Funktion zum Entfernen einer Position anhand ihrer ID
  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
    setLastEditedPrice(prev => {
      const updatedTracking = { ...prev };
      delete updatedTracking[id];
      return updatedTracking;
    });
    setProductNameInputs(prev => {
      const updatedInputs = { ...prev };
      delete updatedInputs[id];
      return updatedInputs;
    });
    setShowProductDropdowns(prev => {
      const updatedDropdowns = { ...prev };
      delete updatedDropdowns[id];
      return updatedDropdowns;
    });
  };

  const handleItemChange = (id: string, field: keyof Omit<InvoiceItem, 'id'>, value: any) => {
    // Debug-Logging für erweiterte Felder
    if (['unitCode', 'sellerArticleId', 'buyerArticleId', 'globalId', 'globalIdScheme'].includes(field)) {
      console.log('DEBUG handleItemChange called:', { id, field, value });
    }
    
    // Bestimme lastEditedType vor dem State-Update
    let lastEditedType: 'net' | 'gross' | undefined = undefined;
    
    setItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          let updatedItem = { ...item, [field]: value };

          // Debug-Logging für erweiterte Felder
          if (['unitCode', 'sellerArticleId', 'buyerArticleId', 'globalId', 'globalIdScheme'].includes(field)) {
            console.log('DEBUG updating item:', { 
              itemId: id, 
              field, 
              oldValue: item[field], 
              newValue: value,
              updatedItem 
            });
          }

          // Keep price recalculation logic, but don't update state here
          if (field === 'netPrice') {
             updatedItem.grossPrice = calculateGrossPrice(value, item.taxRate);
             lastEditedType = 'net'; // Merke Typ für späteres Update
          } else if (field === 'grossPrice') {
             updatedItem.netPrice = calculateNetPrice(value, item.taxRate);
             lastEditedType = 'gross'; // Merke Typ für späteres Update
          } else if (field === 'taxRate') {
            const currentEditedType = lastEditedPrice[id];
            if(currentEditedType === 'gross' && item.grossPrice !== null && item.grossPrice !== undefined) {
                 updatedItem.netPrice = calculateNetPrice(item.grossPrice, value);
            } else if (item.netPrice !== null && item.netPrice !== undefined) {
                 updatedItem.grossPrice = calculateGrossPrice(item.netPrice, value);
            }
            // Behalte den aktuellen EditedType bei Steueränderungen
          }

          return updatedItem;
        }
        return item;
      });
      
      // Debug-Logging für erweiterte Felder
      if (['unitCode', 'sellerArticleId', 'buyerArticleId', 'globalId', 'globalIdScheme'].includes(field)) {
        console.log('DEBUG setItems result:', updatedItems.find(item => item.id === id));
      }
      
      return updatedItems;
    });
    
    // Aktualisiere lastEditedPrice nur wenn nötig, und getrennt vom setItems-Aufruf
    if (lastEditedType) {
      // Verzögere den setState-Aufruf, um setState während des Renderns zu vermeiden
      setTimeout(() => {
        setLastEditedPrice(prev => ({ ...prev, [id]: lastEditedType as 'net' | 'gross' }));
      }, 0);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedItemId(expandedItemId === id ? null : id);
  };

  // Berechnet den Nettobetrag für eine Position (für Anzeige in der Zeile)
  const calculateItemNetAmount = (item: InvoiceItem): number => {
     return getAccurateItemNetAmount(item, lastEditedPrice); 
  };

  // Berechnet den Steuerbetrag für eine Position (für Anzeige in der Zeile)
  const calculateItemTaxAmount = (item: InvoiceItem): number => {
     return getAccurateItemTaxAmount(item, lastEditedPrice);
  };

  // Formatierungsfunktion für Währung (verbessert)
  const formatCurrency = (amount: number) => {
    // Stellen Sie sicher, dass der Währungscode gültig ist (genau 3 Zeichen)
    const validCurrency = currency.length === 3 ? currency : 'EUR';
    try {
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: validCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Bei Fehler formatieren ohne Währungssymbol (nur Zahl)
      logger.error(error, `Formatierungsfehler mit Währungscode ${validCurrency}`);
      return new Intl.NumberFormat('de-DE', { 
        style: 'decimal',
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount) + ' ' + validCurrency;
    }
  };

  // Handler für Klick auf "Verbessern"-Button
  const handleEnhanceClick = async (itemId: string) => {
    const itemToEnhance = items.find(item => item.id === itemId);
    if (!itemToEnhance || !currentUser) return;

    setEnhancingItemId(itemId); 
    setIsLoadingSuggestion(true);
    setSuggestionError(null);

    try {
      const token = await currentUser.getIdToken();
      // Assuming enhanceInvoiceItemText returns { suggestedName: string, suggestedDescription: string | null }
      const result = await enhanceInvoiceItemText(itemToEnhance.name, itemToEnhance.description || '', token);
      setAiSuggestion({ 
        originalName: itemToEnhance.name,
        originalDescription: itemToEnhance.description,
        // Fix 2: Use correct property names from the API response
        suggestedName: result.suggestedName, 
        suggestedDescription: result.suggestedDescription 
      });
      setShowSuggestionModal(true); 
    } catch (err) {
      logger.error(err, "Error enhancing item text");
      setSuggestionError(err instanceof Error ? err.message : "Fehler bei der KI-Verbesserung.");
      setEnhancingItemId(null); // Bei Fehler zurücksetzen
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  // Helper zum Schließen des Modals und Zurücksetzen des States
  const closeSuggestionModal = () => {
    setShowSuggestionModal(false);
    setAiSuggestion(null);
    setEnhancingItemId(null);
  };
  
  // Handler für das Akzeptieren des Vorschlags
  const handleAcceptSuggestion = () => {
    if (process.env.NODE_ENV === 'development') {
      logger.info(`handleAcceptSuggestion wird aufgerufen. enhancingItemId=${enhancingItemId}, aiSuggestion vorhanden=${!!aiSuggestion}`);
    }
    
    if (!aiSuggestion || !enhancingItemId) {
      logger.error("Kann Vorschlag nicht akzeptieren: kein Vorschlag oder keine Item-ID vorhanden");
      return;
    }
    
    // Debug-Ausgabe für Fehlersuche
    logger.info(`Akzeptiere Vorschlag für Item ID ${enhancingItemId}`, { 
      suggestion: aiSuggestion, 
      itemExists: items.some(item => item.id === enhancingItemId)
    });
    
    try {
      // Finde den Index des zu aktualisierenden Items
      const itemIndex = items.findIndex(item => item.id === enhancingItemId);
      if (itemIndex === -1) {
        logger.error(`Item mit ID ${enhancingItemId} nicht gefunden`);
        return;
      }
      
      // Erstelle ein neues Array mit den aktualisierten Items
      const updatedItems = [...items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        name: aiSuggestion.suggestedName,
        description: aiSuggestion.suggestedDescription || ''
      };
      
      // Aktualisiere die Items über den State-Setter
      logger.info(`Aktualisiere items mit neuem Namen "${aiSuggestion.suggestedName}" für Item ID ${enhancingItemId}`);
      setItems(updatedItems);
      
      // HINZUFÜGEN: Aktualisiere auch productNameInputs, damit der Name im Formular angezeigt wird
      logger.info(`Aktualisiere productNameInputs mit neuem Namen "${aiSuggestion.suggestedName}" für Item ID ${enhancingItemId}`);
      setProductNameInputs(prev => ({
        ...prev,
        [enhancingItemId]: aiSuggestion.suggestedName
      }));
      
      // Modal schließen
      logger.info('Schließe Suggestion-Modal');
      closeSuggestionModal();
    } catch (error) {
      logger.error("Fehler beim Akzeptieren des Vorschlags:", error);
    }
  };

  // Handler für das Ablehnen des Vorschlags / Schließen des Modals
  const handleRejectSuggestion = () => {
    closeSuggestionModal();
  };

  // --- Autocomplete States für Produktnamen ---
  const handleProductNameChange = (id: string, value: string) => {
    setProductNameInputs(prev => ({ ...prev, [id]: value }));
    handleItemChange(id, 'name', value); // Ruft die Logik zum Setzen der itemAction auf
    setShowProductDropdowns(prev => ({ ...prev, [id]: true }));
  };

  // Wiederherstellen und Korrigieren der Logik zur Produktauswahl
  const handleSelectProductSuggestion = (itemId: string, suggestion: ProductData) => {
    nativeConsole.log(`Selected product suggestion for item ${itemId}:`, suggestion);
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          // Determine the price and tax rate to apply from the suggestion or keep existing
          const newNetPrice = suggestion.netPrice ?? item.netPrice;
          const newTaxRate = suggestion.taxRate ?? item.taxRate;

          // Create the updated item, focusing on applying suggestion data robustly
          // Aber: Bereits manuell eingegebene erweiterte Felder NICHT überschreiben
          const updatedItem = {
            ...item, // Spread existing item first to keep other fields (like quantity)
            name: suggestion.name || '', // Use suggestion name
            description: suggestion.description || suggestion.name || '', // Use description or fallback to name
            
            // Apply the determined price and tax rate
            netPrice: newNetPrice,
            taxRate: newTaxRate,
            
            // Erweiterte Felder: Produktdaten haben Vorrang vor Standardwerten
            unitCode: suggestion.unitCode || item.unitCode,
            sellerArticleId: suggestion.sellerArticleId || item.sellerArticleId || null,
            buyerArticleId: suggestion.buyerArticleId || item.buyerArticleId || null,
            globalId: suggestion.globalId || item.globalId || null,
            globalIdScheme: suggestion.globalIdScheme || item.globalIdScheme || null,

            // Explicitly add/update productId if it exists in the suggestion
            ...(suggestion.productId && { productId: suggestion.productId }),
            
            // Calculate grossPrice based on the NEW netPrice and taxRate
            grossPrice: calculateGrossPrice(newNetPrice, newTaxRate),
            
            // Produkt wurde gerade geladen, keine Aktion nötig (wird ggf. später vom Hook bestimmt)
            // itemAction: 'none' as 'none' | 'create' | 'update' 
          };
          // Ensure required fields like quantity are preserved (already handled by spread)
          
          return updatedItem;
        }
        return item;
      })
    );
    // Update last edited price tracking to 'net' to ensure net price display is prioritized
    setLastEditedPrice(prev => ({ ...prev, [itemId]: 'net' }));
    // Dropdown für dieses Item schließen und Input leeren/aktualisieren
    setShowProductDropdowns(prev => ({ ...prev, [itemId]: false }));
    // Setze den Namen aus der Suggestion direkt in den kontrollierten Input
    setProductNameInputs(prev => ({ ...prev, [itemId]: suggestion.name || '' }));

    // Call the hook's handler to store the original product data
    hookHandleProductSelection(itemId, suggestion);
    
    // NEU: Rufe auch den externen Callback auf, falls übergeben
    if (onProductSelect) {
      onProductSelect(itemId, suggestion);
    }
  };

  return (
    <div className={showTitle ? "p-4 border border-hairline rounded-md shadow-sm bg-surface-raised mb-6" : ""}>
      {showTitle && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-primary">{t('positions.title')}</h2>
        </div>
      )}

      {items.map((item, index) => (
        <ItemRow
          key={item.id}
          item={item}
          onItemChange={handleItemChange}
          onRemove={handleRemoveItem}
          currency={currency}
          productNameInput={productNameInputs[item.id] ?? item.name}
          setProductNameInput={(value) => {
            if (process.env.NODE_ENV === 'development') {
              logger.info(`setProductNameInput aufgerufen für Item ${item.id} mit Wert "${value}"`);
            }
            handleProductNameChange(item.id, value);
          }}
          showProductDropdown={showProductDropdowns[item.id] ?? false}
          setShowProductDropdown={(show) => setShowProductDropdowns(prev => ({...prev, [item.id]: show}))}
          onSelectProductSuggestion={(suggestion) => handleSelectProductSuggestion(item.id, suggestion)}
          userId={effectiveScopeUid}
          expanded={expandedItemId === item.id}
          onToggleExpand={toggleExpand}
          lastEditedPrice={lastEditedPrice[item.id]}
          onEnhanceClick={(id) => {
            if (process.env.NODE_ENV === 'development') {
              logger.info(`onEnhanceClick aufgerufen für Item ${id}`);
            }
            handleEnhanceClick(id);
          }}
          isLoadingSuggestion={isLoadingSuggestion && enhancingItemId === item.id}
          index={index}
          formatCurrency={formatCurrency}
          calculateItemNetAmount={calculateItemNetAmount}
          calculateGrossPrice={calculateGrossPrice}
          calculateNetPrice={calculateNetPrice}
          t={t}
          buyerVatId={buyerVatId}
        />
      ))}

      {/* "Position hinzufügen"-Button */}
      <button 
        type="button" 
        onClick={handleAddItem} 
        className="mt-2 px-4 py-2 border border-indigo-500 text-accent-ink rounded-md hover:bg-[var(--tone-accent-bg)] transition text-sm font-medium"
      >
                    {t('positions.addPosition')}
      </button>

      {/* Gesamtsummen - Währung hinzugefügt */}
      <div className="mt-6 pt-4 border-t border-hairline">
                        <h3 className="text-lg font-semibold mb-3 text-secondary">{t('totals.title')}</h3>
        <div className="space-y-2 text-sm">
          {/* Display net amounts per tax rate (rounded) */}
           {Object.entries(taxesByRate).map(([rate, amounts]) => (
             <div key={rate} className="flex justify-between text-secondary">
                                 <span>{t('totals.subtotalVat', { rate })}</span>
               <span>{formatCurrency(roundToTwoDecimals(amounts.net))}</span>
             </div>
           ))}
          <div className="flex justify-between text-secondary">
                            <span>{t('totals.totalNet')}</span>
            {/* Use the derived displayNetTotal */}
            <span>{formatCurrency(displayNetTotal)}</span>
          </div>
           {/* Display tax amounts per tax rate (rounded) */}
           {Object.entries(taxesByRate).map(([rate, amounts]) => (
             amounts.tax > 0 && (
                 <div key={rate} className="flex justify-between text-secondary">
                  <span>{t('totals.vat')} ({rate}%)</span>
                  <span>{formatCurrency(roundToTwoDecimals(amounts.tax))}</span>
                 </div>
             )
          ))}
           <div className="flex justify-between font-bold text-primary pt-2 border-t border-strong">
                          <span>{t('totals.totalGross')}</span>
             {/* Use the final rounded displayGrossTotal */}
            <span>{formatCurrency(displayGrossTotal)}</span>
          </div>
        </div>
      </div>

      {/* Modal für Vorschläge */}
      {showSuggestionModal && aiSuggestion && (
        <div className="fixed inset-0 bg-surface-hover bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('aiSuggestion.title')}</h3>
            <div className="mt-2 space-y-4">
               {suggestionError ? (
                   <p className="text-red-600">{suggestionError}</p>
               ) : aiSuggestion ? (
                 <>
                   <div>
                      <p className="text-sm font-medium text-faint">{t('aiSuggestion.originalName')}</p>
                      <p className="text-sm text-gray-800 p-2 bg-gray-100 rounded">{aiSuggestion.originalName}</p>
                                              <p className="text-sm font-medium text-faint mt-2">{t('aiSuggestion.suggestedName')}</p>
                      <p className="text-sm text-green-800 p-2 bg-green-50 rounded font-semibold">{aiSuggestion.suggestedName}</p>
                   </div>
                   { (aiSuggestion.originalDescription || aiSuggestion.suggestedDescription) &&
                     <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-medium text-faint">{t('aiSuggestion.originalDescription')}</p>
                        <p className="text-sm text-gray-800 p-2 bg-gray-100 rounded">{aiSuggestion.originalDescription || '-'}</p>
                                                  <p className="text-sm font-medium text-faint mt-2">{t('aiSuggestion.suggestedDescription')}</p>
                        <p className="text-sm text-green-800 p-2 bg-green-50 rounded font-semibold">{aiSuggestion.suggestedDescription || '-'}</p>
                     </div>
                   }
                </>
               ) : (
                 <p>Lade Vorschlag...</p> // Sollte nicht angezeigt werden, da Modal erst bei Daten/Fehler kommt
               )}
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              {!suggestionError && aiSuggestion && (
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
                  onClick={() => {
                    logger.info("Annehmen-Button im Modal geklickt");
                    handleAcceptSuggestion();
                  }}
                >
                  {t('aiSuggestion.accept')}
                </button>
              )}
              <button
                type="button"
                className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 ${suggestionError || !aiSuggestion ? 'sm:col-start-1 sm:col-span-2' : 'sm:col-start-1'} sm:text-sm`}
                onClick={handleRejectSuggestion}
              >
                                  {suggestionError ? t('aiSuggestion.close') : t('aiSuggestion.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsForm; 